import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { PROGRAMS } from "@/pages/Apply";

const LABEL = Object.fromEntries(PROGRAMS);
const when = (iso) => new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });

const useList = (url) => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get(url).then((r) => setRows(r.data));
  }, [url]);
  return rows;
};

const InviteButton = ({ app }) => {
  const [invitedAt, setInvitedAt] = useState(app.invited_at);
  const [busy, setBusy] = useState(false);
  const invite = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/applications/${app.id}/invite`);
      setInvitedAt(data.invited_at);
      toast.success(`Your invitation is on its way to ${app.full_name.split(" ")[0]}.`);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-4 flex flex-wrap items-center gap-3">
      <button onClick={invite} disabled={busy} data-testid={`invite-ambassador-btn-${app.id}`} className={`btn !py-2 !px-5 text-xs ${invitedAt ? "btn-ghost" : "btn-olive"}`}>
        {busy ? "Sending…" : invitedAt ? "Send Invitation Again" : "Invite to the Ambassadors"}
      </button>
      {invitedAt && <span className="text-xs text-[#577731]" data-testid={`invited-at-${app.id}`}>Invited {when(invitedAt)}</span>}
    </div>
  );
};

export const AdminApplications = () => {
  const rows = useList("/admin/applications");
  if (!rows) return null;
  if (!rows.length) return <p className="text-[#232b1b]/60">No applications yet.</p>;
  return (
    <div className="space-y-4" data-testid="admin-applications">
      {rows.map((a) => (
        <article key={a.id} className="card-soft p-6 grid md:grid-cols-3 gap-4 text-sm" data-testid={`application-${a.id}`}>
          <div>
            <p className="font-serif-h text-2xl">{a.full_name}</p>
            <p className="text-[#232b1b]/70">{a.email} · {a.phone}</p>
            <p className="text-xs text-[#232b1b]/50 mt-1">{when(a.created_at)}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-[#b6689a] mb-2">Paths</p>
            <div className="flex flex-wrap gap-2">{a.programs.map((p) => <span key={p} className="rounded-full bg-[#b1cc90]/35 px-3 py-1 text-xs">{LABEL[p] || p}</span>)}</div>
            <p className="mt-3 text-[#232b1b]/75"><span className="font-medium">Found us:</span> {a.heard_about || "—"}</p>
            <InviteButton app={a} />
          </div>
          {a.programs.includes("womb") && (
            <div className="rounded-2xl bg-[#b6689a]/10 p-4">
              <p className="text-xs uppercase tracking-widest text-[#b6689a] mb-2">Healing Womb</p>
              <p>Date of birth: {a.dob || "—"}</p>
              <p>Location: {a.location || "—"}</p>
              <p>Postpartum: {a.postpartum || "—"}</p>
              <p>Children: {a.children || "—"}</p>
              <p>Doula: {a.doula || "—"}</p>
            </div>
          )}
        </article>
      ))}
    </div>
  );
};

export const AdminSubscribers = () => {
  const rows = useList("/admin/subscribers");
  if (!rows) return null;
  if (!rows.length) return <p className="text-[#232b1b]/60">No tea subscribers yet.</p>;
  return (
    <div className="card-soft overflow-x-auto" data-testid="admin-subscribers">
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase tracking-widest text-[#232b1b]/60">
          <tr>{["First name", "Email", "Phone", "Joined"].map((h) => <th key={h} className="px-5 py-4">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.id} className="border-t border-[#dacac1]" data-testid={`subscriber-row-${s.id}`}>
              <td className="px-5 py-3">{s.first_name}</td>
              <td className="px-5 py-3">{s.email}</td>
              <td className="px-5 py-3">{s.phone || "—"}</td>
              <td className="px-5 py-3 text-[#232b1b]/60">{when(s.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const AdminRsvps = () => {
  const rows = useList("/admin/rsvps");
  if (!rows) return null;
  if (!rows.length) return <p className="text-[#232b1b]/60">No RSVPs yet.</p>;
  return (
    <div className="card-soft overflow-x-auto" data-testid="admin-rsvps">
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase tracking-widest text-[#232b1b]/60">
          <tr>{["Ceremony", "Name", "Phone", "Email", "Guests", "Received"].map((h) => <th key={h} className="px-5 py-4">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-[#dacac1]" data-testid={`rsvp-row-${r.id}`}>
              <td className="px-5 py-3 capitalize">{r.ceremony === "light" ? "Light Ceremony" : "Waterfall"}</td>
              <td className="px-5 py-3">{r.name}</td>
              <td className="px-5 py-3">{r.phone}</td>
              <td className="px-5 py-3">{r.email}</td>
              <td className="px-5 py-3">{r.guests}</td>
              <td className="px-5 py-3 text-[#232b1b]/60">{when(r.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
